import { CompareHelper, ConvertHelper } from '@barthauer/abc/core';
import {
  ObjectTypeInfo,
  ObjectTypeInfoColumn,
  ObjectTypeInfoColumnType,
  ObjectTypeInfoReference,
  ObjectTypeInfoReferencing,
} from '../models/objectTypeInfo.model';

export class ObjectTypeInfoViewModel {
  private _columnsByName: Map<string, ObjectTypeInfoColumnViewModel>;
  private _columnsByNameInBaseType: Map<string, ObjectTypeInfoColumnViewModel>;
  private _requiredColumnsByName: Map<string, ObjectTypeInfoColumnViewModel>;

  private _referencesByName: Map<string, ObjectTypeInfoReferenceViewModel>;
  private _referencesByColumn: Map<string, ObjectTypeInfoReferenceViewModel>;

  private _referencingByName: Map<string, ObjectTypeInfoReferencingViewModel>;
  private _derivedTypes: ObjectTypeInfoViewModel[];
  private _parentTypes: Map<string, ObjectTypeInfoViewModel> = new Map();
  private viewObjectGetter;
  private infoObject: ObjectTypeInfo;
  private allInfoViewObjectsByBaseTypeGetter;
  private allInfoViewObjectsByChildTypeGetter;
  private _objectReferenceInfoColumns: { id: ObjectTypeInfoColumnViewModel; objectType: ObjectTypeInfoColumnViewModel };

  public get objectReferenceInfoColumns(): {
    id: ObjectTypeInfoColumnViewModel;
    objectType: ObjectTypeInfoColumnViewModel;
  } {
    if (CompareHelper.isEmptyObject(this._objectReferenceInfoColumns)) {
      this.objectReferenceInfoColumns = this.baseType?.objectReferenceInfoColumns;
    }
    return this._objectReferenceInfoColumns;
  }

  public set objectReferenceInfoColumns(value: {
    id: ObjectTypeInfoColumnViewModel;
    objectType: ObjectTypeInfoColumnViewModel;
  }) {
    this._objectReferenceInfoColumns = value;
  }

  get name(): string {
    return this.infoObject.Name;
  }
  get displayText(): string {
    return this.infoObject.DisplayText;
  }
  get displayTextPlural(): string {
    return this.infoObject.DisplayTextPlural;
  }

  get uniqueDisplayText(): string {
    return this.infoObject.UniqueDisplayText;
  }
  get namespace(): string {
    return this.infoObject.Namespace;
  }
  get value(): number {
    return parseInt(this.infoObject.Value);
  }
  get baseType(): ObjectTypeInfoViewModel {
    return this.viewObjectGetter(this.infoObject.BaseTypeName);
  }
  get mainType(): ObjectTypeInfoViewModel {
    let current: ObjectTypeInfoViewModel = this;
    let base: ObjectTypeInfoViewModel = current.baseType;
    while (base) {
      current = base;
      base = current.baseType;
    }
    return current;
  }

  get columnsByName(): Map<string, ObjectTypeInfoColumnViewModel> {
    if (!this._columnsByName) {
      this._columnsByName = new Map<string, ObjectTypeInfoColumnViewModel>();
      this._columnsByNameInBaseType = new Map<string, ObjectTypeInfoColumnViewModel>();
      for (const column of this.infoObject.Columns) {
        const oticvm = new ObjectTypeInfoColumnViewModel(
          (name) => this.referencesByColumn[name],
          (name) => this.baseType?.columnsByName.get(name),
          column
        );
        this._columnsByName.set(column.Name, oticvm);
        if (column.NameInBaseType) {
          this._columnsByNameInBaseType.set(column.NameInBaseType, oticvm);
        }
      }
    }

    return this._columnsByName;
  }

  get columnsByNameInBaseType(): Map<string, ObjectTypeInfoColumnViewModel> {
    if (!this._columnsByNameInBaseType) {
      this.columnsByName;
    }
    return this._columnsByNameInBaseType;
  }

  get referencesByName(): Map<string, ObjectTypeInfoReferenceViewModel> {
    if (!this._referencesByName) {
      this.initReferences();
    }

    return this._referencesByName;
  }

  get referencesByColumn(): Map<string, ObjectTypeInfoReferenceViewModel> {
    if (!this._referencesByColumn) {
      this.initReferences();
    }

    return this._referencesByColumn;
  }

  get referencingByName(): Map<string, ObjectTypeInfoReferencingViewModel> {
    if (!this._referencingByName) {
      this._referencingByName = new Map<string, ObjectTypeInfoReferencingViewModel>();
      for (const ref of this.infoObject.Referencing) {
        this._referencingByName.set(ref.Name, new ObjectTypeInfoReferencingViewModel(this.viewObjectGetter, ref));
      }
    }

    return this._referencingByName;
  }

  get parentTypes(): Map<string, ObjectTypeInfoViewModel> {
    if (this._parentTypes?.size === 0) {
      const allInfoViewObjectsByChildType = this.allInfoViewObjectsByChildTypeGetter();
      if (allInfoViewObjectsByChildType[this.name]) {
        this._parentTypes = allInfoViewObjectsByChildType[this.name];
      }
    }
    return this._parentTypes;
  }

  get derivedTypes(): ObjectTypeInfoViewModel[] {
    if (!this._derivedTypes) {
      const allInfoViewObjectsByBaseType = this.allInfoViewObjectsByBaseTypeGetter();
      if (allInfoViewObjectsByBaseType[this.name]) {
        this._derivedTypes = allInfoViewObjectsByBaseType[this.name];
      }
    }
    return this._derivedTypes;
  }
  get allDerivedTypes(): ObjectTypeInfoViewModel[] {
    const allTypes: ObjectTypeInfoViewModel[] = new Array<ObjectTypeInfoViewModel>();
    this.flattenTypes(this.derivedTypes, allTypes);
    return allTypes;
  }

  private flattenTypes(types: ObjectTypeInfoViewModel[], result: ObjectTypeInfoViewModel[]) {
    if (types) {
      for (const type of types) {
        result.push(type);
        this.flattenTypes(type.derivedTypes, result);
      }
    }
  }

  get requiredColumnsByName(): Map<string, ObjectTypeInfoColumnViewModel> {
    if (!this._requiredColumnsByName) {
      this._requiredColumnsByName = new Map();
      for (const iterator of this.columnsByName) {
        const otivm: ObjectTypeInfoColumnViewModel = iterator[1];
        if (otivm?.isRequired)
          if (
            !otivm.columnType?.startsWith(ObjectTypeInfoColumnType.Sys) &&
            otivm.columnType !== ObjectTypeInfoColumnType.PrimaryKey &&
            otivm.columnType !== ObjectTypeInfoColumnType.ObjectType
          ) {
            this._requiredColumnsByName.set(otivm.name, otivm);
          }
      }
    }
    return this._requiredColumnsByName;
  }

  constructor(
    infoObject: ObjectTypeInfo,
    viewObjectGetter,
    allInfoViewObjectsByBaseTypeGetter,
    allInfoViewObjectsByChildTypeGetter
  ) {
    this.infoObject = infoObject;
    this.viewObjectGetter = viewObjectGetter;
    this.allInfoViewObjectsByBaseTypeGetter = allInfoViewObjectsByBaseTypeGetter;
    this.allInfoViewObjectsByChildTypeGetter = allInfoViewObjectsByChildTypeGetter;
    this.initReferences();
  }

  private initReferences() {
    this._referencesByName = new Map<string, ObjectTypeInfoReferenceViewModel>();
    this._referencesByColumn = new Map<string, ObjectTypeInfoReferenceViewModel>();

    for (const ref of this.infoObject.References) {
      const objectTypeInfoReference = new ObjectTypeInfoReferenceViewModel(
        (name) => this.getColumnByName(name),
        this.viewObjectGetter,
        ref
      );

      const refObjectTypeColumnName = ref.ReferencedObjectTypeName + '_' + ref.ReferencingObjectTypeColumn;
      const referenceName: string = objectTypeInfoReference.name ?? refObjectTypeColumnName;
      this._referencesByName.set(referenceName, objectTypeInfoReference);
      if (objectTypeInfoReference.column) {
        this._referencesByColumn.set(objectTypeInfoReference.column.name, objectTypeInfoReference);

        if (ref.ReferencingObjectTypeColumn) {
          this.objectReferenceInfoColumns = {
            id: this.columnsByName.get(objectTypeInfoReference.column.name),
            objectType: this.referencesByName.get(referenceName).objectTypeColumn,
          };
        }
      } else {
        console.warn(objectTypeInfoReference);
      }
    }
  }

  private getColumnByName(name: string) {
    return this.columnsByName.get(name) ?? this.baseType?.getColumnByName(name);
  }

  public isMatch(objectType: string | number): boolean {
    return this.value === objectType || this.name === objectType || this.value?.toString() === objectType;
  }

  public isMatchOrBase(objectType: string | number): boolean {
    return this.isMatch(objectType) || this.baseType?.isMatchOrBase(objectType) === true;
  }

  public prepareDisplayTextPlural(text: string): string {
    if (!text || text === '') {
      return this.displayTextPlural;
    }
    const arr = text?.split('\\');
    text = arr[arr.length - 1];
    if (text === this.name || text === this.displayText) {
      text = this.displayTextPlural;
    }
    return text;
  }
}

export class ObjectTypeInfoColumnViewModel {
  private referenceGetter;
  private baseColumnGetter: (name: string) => ObjectTypeInfoColumnViewModel;
  public readonly infoObject: ObjectTypeInfoColumn;

  get displayText(): string {
    return this.infoObject.DisplayText;
  }
  get name(): string {
    return this.infoObject.Name;
  }
  get columnType(): ObjectTypeInfoColumnType {
    return this.infoObject.ColumnType;
  }
  get isRequired(): boolean {
    return ConvertHelper.toBoolean(this.infoObject.IsRequired);
  }
  get reference(): ObjectTypeInfoReferenceViewModel {
    return this.referenceGetter(this.name);
  }

  get baseColumn(): ObjectTypeInfoColumnViewModel {
    return this.baseColumnGetter(this.infoObject.NameInBaseType);
  }

  constructor(referenceGetter, baseColumnGetter, infoObject: ObjectTypeInfoColumn) {
    this.referenceGetter = referenceGetter;
    this.baseColumnGetter = baseColumnGetter;

    if (infoObject instanceof ObjectTypeInfoColumn) {
      this.infoObject = infoObject;
    }
  }
}

export class ObjectTypeInfoReferenceViewModel {
  private columnGetter: (name: string) => ObjectTypeInfoColumnViewModel;
  private viewObjectGetter;

  private infoObject: ObjectTypeInfoReference;

  get displayText(): string {
    return this.infoObject.DisplayText;
  }
  get name(): string {
    return this.infoObject.Name;
  }
  get column(): ObjectTypeInfoColumnViewModel {
    return this.columnGetter(this.infoObject.ReferencingColumn);
  }
  get objectType(): ObjectTypeInfoViewModel {
    return this.viewObjectGetter(this.infoObject.ReferencedObjectTypeName);
  }

  get objectTypeColumn(): ObjectTypeInfoColumnViewModel {
    return this.columnGetter(this.infoObject.ReferencingObjectTypeColumn);
  }

  constructor(columnGetter, viewObjectGetter, infoObject: ObjectTypeInfoReference) {
    this.columnGetter = columnGetter;
    this.viewObjectGetter = viewObjectGetter;
    this.infoObject = infoObject;
  }
}

export class ObjectTypeInfoReferencingViewModel {
  private viewObjectGetter;
  private infoObject: ObjectTypeInfoReferencing;

  get displayText(): string {
    return this.infoObject.DisplayText;
  }
  get name(): string {
    return this.infoObject.Name;
  }
  get column(): ObjectTypeInfoColumnViewModel {
    return this.objectType.columnsByName.get(this.infoObject.ReferencingColumn);
  }
  get objectType(): ObjectTypeInfoViewModel {
    return this.viewObjectGetter(this.infoObject.ReferencingObjectTypeName);
  }

  constructor(viewObjectGetter, infoObject: ObjectTypeInfoReferencing) {
    this.viewObjectGetter = viewObjectGetter;
    this.infoObject = infoObject;
  }
}
